import * as rd from '@devexperts/remote-data-ts'
import { useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'

import { useAppDispatch, useAppSelector } from '@/hooks/store'
import { bookingAvailabilitySlice } from '@/store/bookingAvailabilitySlice'
import { cartSlice } from '@/store/cartSlice'
import { restorePendingCart } from '@/store/cartSlice/thunks/restorePendingCart'
import { insertPeriodSlice } from '@/store/insertPeriodSlice'
import { licenseSlice } from '@/store/licenseSlice'
import { mapSelectors } from '@/store/mapSlice'
import { getBookingFlowFromSearchParams } from '@/utils/bookingFlow'
import { findMapElementIdForReservation } from '@/utils/mapReservationModelsToQuoteReservations'
import {
  getPendingCartPeriod,
  getPendingCartToken,
  isPendingCartPeriodMatch,
} from '@/utils/pendingCartToken'

export function useRestorePendingCart() {
  const dispatch = useAppDispatch()
  const [searchParams] = useSearchParams()
  const restoreAttempted = useRef(false)

  const licenseRemoteData = useAppSelector(
    licenseSlice.selectors.licenseRemoteData
  )
  const licenseCode = useAppSelector(licenseSlice.selectors.licenseCode)
  const cartReservations = useAppSelector(cartSlice.selectors.cartReservations)
  const isCartPaid = useAppSelector(cartSlice.selectors.isCartPaid)

  useEffect(() => {
    if (!rd.isSuccess(licenseRemoteData) || restoreAttempted.current) {
      return
    }

    const pendingCartToken = getPendingCartToken(licenseCode)
    if (!pendingCartToken || isCartPaid || cartReservations.length > 0) {
      return
    }

    restoreAttempted.current = true

    const restore = async () => {
      try {
        const { periodWasRestored, shouldRestoreCart } =
          await dispatch(restorePendingCart()).unwrap()

        if (!periodWasRestored && !shouldRestoreCart) {
          return
        }

        const bookingFlow = getBookingFlowFromSearchParams(searchParams)
        await dispatch(
          bookingAvailabilitySlice.actions.load({ bookingFlow })
        ).unwrap()

        if (shouldRestoreCart) {
          await dispatch(
            cartSlice.actions.quote({ needsToUpdateCartTotal: true })
          ).unwrap()
        }
      } catch {
        // Token cleared in restorePendingCart on API failure
      }
    }

    void restore()
  }, [
    cartReservations.length,
    dispatch,
    isCartPaid,
    licenseCode,
    licenseRemoteData,
    searchParams,
  ])
}

export function useClearCartOnPendingPeriodChange() {
  const dispatch = useAppDispatch()
  const licenseCode = useAppSelector(licenseSlice.selectors.licenseCode)
  const period = useAppSelector(insertPeriodSlice.selectors.period)
  const bookingPeriod = useAppSelector(
    insertPeriodSlice.selectors.bookingPeriod
  )
  const previousPeriodKey = useRef<string | null>(null)

  useEffect(() => {
    const pendingPeriod = getPendingCartPeriod(licenseCode)

    if (!getPendingCartToken(licenseCode) || !pendingPeriod || !period) {
      return
    }

    const currentPeriodKey = `${period.start}|${period.end}|${bookingPeriod}`

    if (previousPeriodKey.current === null) {
      previousPeriodKey.current = currentPeriodKey

      if (!isPendingCartPeriodMatch(period, bookingPeriod, pendingPeriod)) {
        dispatch(cartSlice.actions.resetCartSlice())
      }

      return
    }

    if (previousPeriodKey.current === currentPeriodKey) {
      return
    }

    previousPeriodKey.current = currentPeriodKey

    if (!isPendingCartPeriodMatch(period, bookingPeriod, pendingPeriod)) {
      dispatch(cartSlice.actions.resetCartSlice())
    }
  }, [bookingPeriod, dispatch, licenseCode, period])
}

export function useSyncRestoredCartItems() {
  const dispatch = useAppDispatch()
  const cartReservations = useAppSelector(cartSlice.selectors.cartReservations)
  const cartItems = useAppSelector(cartSlice.selectors.items)
  const mapRemoteData = useAppSelector(mapSelectors.mapRemoteData)
  const licenseCode = useAppSelector(licenseSlice.selectors.licenseCode)

  useEffect(() => {
    if (
      !rd.isSuccess(mapRemoteData) ||
      cartReservations.length === 0 ||
      Object.keys(cartItems).length > 0
    ) {
      return
    }

    if (!getPendingCartToken(licenseCode)) {
      return
    }

    cartReservations.forEach((reservation) => {
      const elementId = findMapElementIdForReservation(
        mapRemoteData.value.elements ?? [],
        reservation
      )

      if (elementId !== null) {
        dispatch(cartSlice.actions.setCartItem({ id: elementId, quantity: 1 }))
      }
    })
  }, [cartItems, cartReservations, dispatch, licenseCode, mapRemoteData])
}
